import { FC, useEffect, useMemo, useState } from "react";
import {
	Box,
	LinearProgress,
	Pagination,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useUserStore } from "../../../../../stores/user.store";
import useEventActivityReport from "../../../../../hooks/useEventActivityReport";
import ReportFiltersBar, {
	ReportFilters,
	toApiDate,
} from "../../components/ReportFiltersBar";
import EventActivityKpis from "./EventActivityKpis";
import { useParticipantColumn } from "../../useParticipantColumn";
import EventActivityPivotTable from "./EventActivityPivotTable";

/**
 * SIMS-5825: the four report filters are owned by the page and shared by every
 * tab, so the single Cover sheet in the export describes the whole workbook
 * truthfully.
 */
interface EventActivityTabProps {
	programId: string;
	filters: ReportFilters;
	onFiltersChange: (next: ReportFilters) => void;
}

const EventActivityTab: FC<EventActivityTabProps> = ({
	programId,
	filters,
	onFiltersChange,
}) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const participant = useParticipantColumn();

	const [page, setPage] = useState<number>(0);
	const PAGE_SIZE = 25;

	// A filter change invalidates the current page.
	useEffect(() => {
		setPage(0);
	}, [filters]);

	const { data, isLoading, isFetching } = useEventActivityReport({
		accountId,
		programId,
		startDate: toApiDate(filters.startDate),
		endDate: toApiDate(filters.endDate),
		pageNumber: page,
		pageSize: PAGE_SIZE,
	});

	// SIMS-8895 (5): resolved here rather than in the pivot table, so that
	// component stays presentational and keeps taking its rows as data.
	const rows = useMemo(
		() =>
			(data?.rows ?? []).map((row) => ({
				...row,
				participantName: participant.display(
					row.participantName,
					row.participantCin,
				),
			})),
		[data?.rows, participant],
	);

	const hasNoEvents = data && data.events.length === 0;
	const pageCount = data
		? Math.max(1, Math.ceil(data.pagination.total / data.pagination.pageSize))
		: 1;

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="event-activity"
				filters={filters}
				onChange={onFiltersChange}
				hiddenFilters={["orgUnit", "researcher"]}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{hasNoEvents ? (
				<Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
					<Typography variant="h6" gutterBottom>
						No events scheduled for this program
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Configure events under the program&apos;s treatment plan
						(SmartProgram workflow, days 1–14) to start tracking activity.
					</Typography>
				</Paper>
			) : (
				<>
					<EventActivityKpis
						kpis={
							data?.kpis ?? {
								expected: 0,
								submitted: 0,
								missed: 0,
								pending: 0,
							}
						}
					/>

					<EventActivityPivotTable events={data?.events ?? []} rows={rows} />

					{data && data.pagination.total > data.pagination.pageSize && (
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Pagination
								count={pageCount}
								page={page + 1}
								onChange={(_e, p) => setPage(p - 1)}
								color="primary"
								size="small"
							/>
						</Box>
					)}
				</>
			)}
		</Stack>
	);
};

export default EventActivityTab;
